import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { Button, Input, Table, Tag, Spin, Toast, Tabs, Space, Empty, Dropdown, Select } from '@douyinfe/semi-ui';
import { IconSearch, IconDownload, IconHistory, IconDelete } from '@douyinfe/semi-icons';
import { API, timestamp2string } from '../helpers';
import { stringToColor, renderQuota, renderModelPrice } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import { Tooltip, Modal, Popover } from '@douyinfe/semi-ui';
import Papa from 'papaparse';
import axios from 'axios';

import { FilterToolbar } from './logs';

// 懒加载 ECharts 减少首屏加载
const ReactECharts = lazy(() => import('echarts-for-react'));

const { TabPane } = Tabs;

function renderTimestamp(timestamp) {
    return timestamp2string(timestamp);
}

function renderIsStream(bool) {
    if (bool) {
        return <Tag color="blue" size="large">流</Tag>;
    } else {
        return <Tag color="purple" size="large">非流</Tag>;
    }
}

function renderUseTime(type) {
    const time = parseInt(type);
    if (time < 101) {
        return <Tag color="green" size="large"> {time} 秒 </Tag>;
    } else if (time < 300) {
        return <Tag color="orange" size="large"> {time} 秒 </Tag>;
    } else {
        return <Tag color="red" size="large"> {time} 秒 </Tag>;
    }
}

// API 服务地址 - 统一使用相对路径 /api：
//   - 生产环境由 Nginx 反代到 wyzai-tools-api (K8s ClusterIP)
//   - 开发环境由 setupProxy.js 反代到生产 https://token.wyzlab.ai
// 如需本地起后端，把 REACT_APP_LOG_API_URL 设成 http://localhost:3016/api
const LOG_API_BASE = process.env.REACT_APP_LOG_API_URL || '/api';

// 查询历史本地存储
const HISTORY_STORAGE_KEY = 'wyzai_query_history';
const HISTORY_MAX = 20;
const loadHistory = () => {
    try {
        const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
};
const saveHistoryToStorage = (list) => {
    try { localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
};
const maskKey = (k) => {
    if (!k) return '';
    if (k.length <= 14) return k;
    return `${k.slice(0, 7)}...${k.slice(-4)}`;
};
const formatHistoryTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
        return `今天 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const LogsTable = () => {
    const [apikey, setAPIKey] = useState('');
    const [activeTabKey, setActiveTabKey] = useState('');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
    const [baseUrl, setBaseUrl] = useState('');
    const [baseUrls, setBaseUrls] = useState({});
    
    // 新增：日期范围和分页状态
    const [dateRange, setDateRange] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [shouldAutoFetch, setShouldAutoFetch] = useState(false);
    
    // 图表切换状态
    const [chartType, setChartType] = useState('pie'); // 'pie' | 'bar'
    const [trendGranularity, setTrendGranularity] = useState('day'); // 'hour' | 'day' | 'week'
    
    // 筛选状态
    const [modelFilter, setModelFilter] = useState(''); // 模型筛选
    const [streamFilter, setStreamFilter] = useState('all'); // 'all' | 'stream' | 'non-stream'
    const [availableModels, setAvailableModels] = useState([]); // 可选模型列表
    
    // 转账记录状态
    const [showTransferHistory, setShowTransferHistory] = useState(false);
    const [transferRecords, setTransferRecords] = useState([]);
    const [transferLoading, setTransferLoading] = useState(false);
    const [transferPagination, setTransferPagination] = useState({ total: 0, page: 1, totalPages: 0 });
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupUpdating, setGroupUpdating] = useState(false);
    
    const TRANSFER_API_BASE = process.env.REACT_APP_TRANSFER_API_URL || 'https://tr.wyzlab.ai';
    const EXCHANGE_RATE = 500000;

    // 查询历史 (localStorage)
    const [history, setHistory] = useState(loadHistory);

    const recordHistory = useCallback((key, balance, usage, valid) => {
        if (!key || !valid) return;
        setHistory((prev) => {
            const filtered = prev.filter((it) => it.key !== key);
            const next = [{ key, balance, usage, time: Date.now() }, ...filtered].slice(0, HISTORY_MAX);
            saveHistoryToStorage(next);
            return next;
        });
    }, []);

    const removeHistoryItem = useCallback((key) => {
        setHistory((prev) => {
            const next = prev.filter((it) => it.key !== key);
            saveHistoryToStorage(next);
            return next;
        });
    }, []);

    const clearHistory = useCallback(() => {
        saveHistoryToStorage([]);
        setHistory([]);
        Toast.success('已清空历史记录');
    }, []);

    const handleHistorySelect = useCallback((key) => {
        setAPIKey(key);
        setShouldAutoFetch(true);
    }, []);

    useEffect(() => {
        // 统一使用迁移后的 Shell API 域名
        setBaseUrls({ "wyzai": "https://shell.wyzlab.ai" });
    }, []);

    useEffect(() => {
        if (!baseUrls || Object.keys(baseUrls).length === 0) {
            setActiveTabKey('');
            setBaseUrl('');
            return;
        }
        const firstKey = Object.keys(baseUrls)[0];
        setActiveTabKey(firstKey);
        setBaseUrl(baseUrls[firstKey]);
    }, [baseUrls]);

    // 日期变化后自动查询
    useEffect(() => {
        if (shouldAutoFetch && apikey && baseUrl) {
            setShouldAutoFetch(false);
            fetchData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldAutoFetch]);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key] || '');
    };

    const resetData = (key) => {
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                balance: 0,
                usage: 0,
                accessdate: "未知",
                logs: [],
                tokenValid: false,
                tokenInfo: null,
                availableGroups: {},
            }
        }));
    };

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        if (!baseUrl) {
            Toast.error('未找到有效的 API 地址');
            return;
        }
        // 检查令牌格式
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }
        setLoading(true);
        let newTabData = {
            ...tabData[activeTabKey],
            balance: 0,
            usage: 0,
            accessdate: 0,
            logs: [],
            tokenValid: false,
            stats: null,
            tokenInfo: null,
            availableGroups: {},
        };

        try {
            // 查询余额信息
            const subscription = await API.get(`${baseUrl}/v1/dashboard/billing/subscription`, {
                headers: { Authorization: `Bearer ${apikey}` },
            });
            const subscriptionData = subscription.data;
            newTabData.balance = subscriptionData.hard_limit_usd;
            newTabData.tokenValid = true;

            let now = new Date();
            let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
            let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
            let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
            const res = await API.get(`${baseUrl}/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`, {
                headers: { Authorization: `Bearer ${apikey}` },
            });
            const data = res.data;
            newTabData.usage = data.total_usage / 100;

            // 查询令牌元数据和可切换分组。高权限凭据只保存在余额服务端，
            // 浏览器只提交当前完整 API Key 作为该令牌的操作凭据。
            try {
                const cleanKey = apikey.trim().replace(/^sk-/, '');
                const tokenRes = await axios.get(`${TRANSFER_API_BASE}/api/token/${cleanKey}`);
                if (tokenRes.data.success) {
                    const tokenInfo = tokenRes.data.data;
                    newTabData.tokenInfo = tokenInfo;
                    newTabData.availableGroups = tokenInfo.available_groups || {};
                    setSelectedGroup(tokenInfo.group || '');
                }
            } catch (tokenInfoError) {
                console.log('查询令牌分组失败:', tokenInfoError);
            }
        } catch (e) {
            console.log(e)
            Toast.error("令牌已用尽");
            resetData(activeTabKey); // 如果发生错误，重置所有数据为默认值
            setLoading(false);
        }
        try {
            // 查询调用详情 - 使用分页 API
            const params = new URLSearchParams({
                key: apikey,
                page: currentPage,
                pageSize: pageSize,
            });
            
            // 添加日期筛选
            if (dateRange && dateRange[0]) {
                params.append('startDate', Math.floor(dateRange[0].getTime() / 1000));
            }
            if (dateRange && dateRange[1]) {
                params.append('endDate', Math.floor(dateRange[1].getTime() / 1000) + 86399); // 当天结束
            }
            // 添加模型筛选
            if (modelFilter) {
                params.append('model', modelFilter);
            }
            // 添加流/非流筛选
            if (streamFilter !== 'all') {
                params.append('stream', streamFilter === 'stream' ? '1' : '0');
            }
            
            const logRes = await API.get(`${LOG_API_BASE}/log/token?${params.toString()}`);
            const { success, data: logData, pagination } = logRes.data;
            if (success) {
                newTabData.logs = logData;
                setTotalLogs(pagination?.total || 0);
                
                // 查询统计数据 (不受分页影响，基于完整数据)
                try {
                    const statsParams = new URLSearchParams({ key: apikey });
                    if (dateRange && dateRange[0]) {
                        statsParams.append('startDate', Math.floor(dateRange[0].getTime() / 1000));
                    }
                    if (dateRange && dateRange[1]) {
                        statsParams.append('endDate', Math.floor(dateRange[1].getTime() / 1000) + 86399);
                    }
                    if (modelFilter) {
                        statsParams.append('model', modelFilter);
                    }
                    if (streamFilter !== 'all') {
                        statsParams.append('stream', streamFilter === 'stream' ? '1' : '0');
                    }
                    const statsRes = await API.get(`${LOG_API_BASE}/log/stats?${statsParams.toString()}`);
                    if (statsRes.data.success) {
                        newTabData.stats = statsRes.data.data;
                        // 更新可用模型列表（仅在无模型筛选时更新，避免被筛选结果覆盖）
                        if (!modelFilter && statsRes.data.data.modelDistribution && statsRes.data.data.modelDistribution.length > 0) {
                            const models = statsRes.data.data.modelDistribution.map(item => item.model_name);
                            setAvailableModels(models);
                        }
                    }
                } catch (statsErr) {
                    console.log('查询统计失败:', statsErr);
                }
            } else {
                Toast.error('查询调用详情失败，请输入正确的令牌');
            }
        } catch (e) {
            console.log('查询日志失败:', e);
            // 降级到原 API
            try {
                const logRes = await API.get(`${baseUrl}/api/log/token?key=${apikey}`);
                const { success, data: logData } = logRes.data;
                if (success) {
                    newTabData.logs = logData.reverse();
                    setTotalLogs(logData.length);
                }
            } catch (fallbackError) {
                Toast.error("查询失败，请输入正确的令牌");
                resetData(activeTabKey);
                setLoading(false);
            }
        }
        setTabData((prevData) => ({
            ...prevData,
            [activeTabKey]: newTabData,
        }));
        setLoading(false);
        recordHistory(apikey, newTabData.balance, newTabData.usage, newTabData.tokenValid);

    };

    const copyText = async (text) => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                Toast.success('已复制：' + text);
                return;
            }
            
            // Fallback for Safari and older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                textArea.remove();
                Toast.success('已复制：' + text);
            } catch (err) {
                textArea.remove();
                Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
            }
        } catch (err) {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const handleGroupUpdate = async () => {
        const currentGroup = activeTabData?.tokenInfo?.group || '';
        if (!selectedGroup || selectedGroup === currentGroup) {
            return;
        }

        setGroupUpdating(true);
        try {
            const cleanKey = apikey.trim().replace(/^sk-/, '');
            const response = await axios.put(
                `${TRANSFER_API_BASE}/api/token/${cleanKey}/group`,
                { group: selectedGroup },
            );
            if (!response.data.success) {
                throw new Error(response.data.message || '分组切换失败');
            }

            const updated = response.data.data;
            setTabData((prevData) => {
                const current = prevData[activeTabKey] || {};
                return {
                    ...prevData,
                    [activeTabKey]: {
                        ...current,
                        tokenInfo: {
                            ...(current.tokenInfo || {}),
                            ...updated,
                        },
                        availableGroups: updated.available_groups || current.availableGroups || {},
                        logs: (current.logs || []).map((log) => ({
                            ...log,
                            token_group: updated.group,
                        })),
                    },
                };
            });
            setSelectedGroup(updated.group);
            Toast.success(response.data.message || `已切换到 ${updated.group} 分组`);
        } catch (error) {
            console.error('切换令牌分组失败:', error);
            Toast.error(error.response?.data?.detail || error.message || '分组切换失败');
        } finally {
            setGroupUpdating(false);
        }
    };

    // 获取转账记录
    const fetchTransferHistory = useCallback(async (page = 1) => {
        if (!apikey || apikey.length < 10) {
            Toast.warning('请先输入有效的令牌');
            return;
        }
        setTransferLoading(true);
        try {
            const cleanKey = apikey.trim().replace(/^sk-/, '');
            const response = await API.get(`${TRANSFER_API_BASE}/api/transfer/history`, {
                params: { key: cleanKey, page, per_page: 20 }
            });
            if (response.data.success) {
                const data = response.data.data;
                setTransferRecords(data.records);
                setTransferPagination({
                    total: data.total,
                    page: data.page,
                    totalPages: data.total_pages
                });
            }
        } catch (error) {
            console.error('查询转账记录失败:', error);
            Toast.error('查询转账记录失败');
        } finally {
            setTransferLoading(false);
        }
    }, [apikey, TRANSFER_API_BASE]);

    // 格式化金额
    const formatUSD = (quota) => {
        if (quota === null || quota === undefined) return '--';
        const usd = quota / EXCHANGE_RATE;
        return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // 转账记录表格列
    const transferColumns = useMemo(() => [
        {
            title: '方向',
            dataIndex: 'direction',
            width: 80,
            render: (direction) => (
                <Tag color={direction === 'in' ? 'green' : 'red'} type="light">
                    {direction === 'in' ? '↓ 转入' : '↑ 转出'}
                </Tag>
            )
        },
        {
            title: '时间',
            dataIndex: 'created_at',
            width: 180,
            render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '--'
        },
        {
            title: '金额',
            dataIndex: 'amount',
            width: 120,
            render: (amount, record) => (
                <span style={{ 
                    color: record.direction === 'in' ? 'var(--semi-color-success)' : 'var(--semi-color-danger)',
                    fontWeight: 500
                }}>
                    {record.direction === 'in' ? '+' : '-'}{formatUSD(amount)}
                </span>
            )
        },
        {
            title: '源令牌',
            dataIndex: 'from_token_name',
            width: 150,
            ellipsis: true,
            render: (name, record) => (
                <div>
                    <div>{name || '--'}</div>
                    <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>...{record.from_key || ''}</div>
                </div>
            )
        },
        {
            title: '目标令牌',
            dataIndex: 'to_token_name',
            width: 150,
            ellipsis: true,
            render: (name, record) => (
                <div>
                    <div>{name || '--'}</div>
                    <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>...{record.to_key || ''}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            render: (status) => (
                <Tag color={status === 'success' ? 'green' : 'red'}>
                    {status === 'success' ? '成功' : '失败'}
                </Tag>
            )
        }
    ], []);

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: renderTimestamp,
            sorter: (a, b) => a.created_at - b.created_at,
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            className: 'table-col--token',
            render: (text, record, index) => {
                const isAvailable = record.type === 0 || record.type === 2;
                if (!isAvailable) {
                    return <></>;
                }

                const tagColor = text && text.toLowerCase().includes('gemini') ? 'cyan' : 'grey';
                const tagContent = text;

                return (
                    <div>
                        <Tag
                            color={tagColor}
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {tagContent}{' '}
                        </Tag>
                    </div>
                );
            },
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
        },
        {
            title: '所属分组',
            dataIndex: 'token_group',
            className: 'table-col--group',
            width: 160,
            render: (group) => {
                const label = group && String(group).trim() ? group : '未设置';
                return (
                    <Tag
                        color={group ? stringToColor(group) : 'grey'}
                        size="large"
                        type="light"
                        className="tag-pill"
                    >
                        {label}
                    </Tag>
                );
            }
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            className: 'table-col--model',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color={stringToColor(text)}
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            width: 140,
            className: 'table-col--duration',
            render: (text, record, index) => {
                return record.model_name?.startsWith('mj_') ? null : (
                    <div>
                        <Space>
                            {renderUseTime(text)}
                            {renderIsStream(record.is_stream)}
                        </Space>
                    </div>
                );
            },
            sorter: (a, b) => a.use_time - b.use_time,
        },
        {
            title: '提示 Tokens',
            dataIndex: 'prompt_tokens',
            width: 120,
            className: 'table-col--number',
            render: (text, record, index) => {
                if (record.model_name?.startsWith('mj_')) return null;
                if (!(record.type === 0 || record.type === 2)) return <></>;
                return <div><span>{text}</span></div>;
            },
            sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
        },
        {
            title: '缓存',
            dataIndex: 'cache_tokens',
            width: 100,
            className: 'table-col--number',
            render: (text, record, index) => {
                if (record.model_name?.startsWith('mj_')) return null;
                if (!(record.type === 0 || record.type === 2)) return <></>;
                
                let cacheTokens = 0;
                let cacheCreationTokens = 0;
                try {
                    const otherData = typeof record.other === 'object' ? record.other : JSON.parse(record.other);
                    cacheTokens = otherData?.cache_tokens || 0;
                    cacheCreationTokens = otherData?.cache_creation_tokens || 0;
                } catch (e) {}
                
                if (cacheTokens === 0 && cacheCreationTokens === 0) {
                    return <span style={{ color: 'var(--semi-color-text-3)', fontSize: 12 }}>—</span>;
                }
                
                const tooltipContent = (
                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                        {cacheTokens > 0 && <div>↓ 命中缓存: {cacheTokens.toLocaleString()}</div>}
                        {cacheCreationTokens > 0 && <div>↑ 创建缓存: {cacheCreationTokens.toLocaleString()}</div>}
                    </div>
                );
                
                return (
                    <Tooltip content={tooltipContent}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6,
                            fontSize: 13,
                        }}>
                            {cacheTokens > 0 && (
                                <span style={{ 
                                    color: '#10b981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}>
                                    <span style={{ fontSize: 10 }}>↓</span>
                                    {cacheTokens >= 1000 ? `${(cacheTokens/1000).toFixed(1)}k` : cacheTokens}
                                </span>
                            )}
                            {cacheCreationTokens > 0 && (
                                <span style={{ 
                                    color: '#3b82f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}>
                                    <span style={{ fontSize: 10 }}>↑</span>
                                    {cacheCreationTokens >= 1000 ? `${(cacheCreationTokens/1000).toFixed(1)}k` : cacheCreationTokens}
                                </span>
                            )}
                        </div>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const getCache = (record) => {
                    try {
                        const otherData = typeof record.other === 'object' ? record.other : JSON.parse(record.other);
                        return (otherData?.cache_tokens || 0) + (otherData?.cache_creation_tokens || 0);
                    } catch (e) { return 0; }
                };
                return getCache(a) - getCache(b);
            },
        },
        {
            title: '补全 Tokens',
            dataIndex: 'completion_tokens',
            width: 140,
            className: 'table-col--number',
            render: (text, record, index) => {
                return parseInt(text) > 0 && (record.type === 0 || record.type === 2) ? (
                    <div>{<span> {text} </span>}</div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => a.completion_tokens - b.completion_tokens,
        },
        {
            title: '花费',
            dataIndex: 'quota',
            width: 160,
            className: 'table-col--number',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? <div>{renderQuota(text, 6)}</div> : <></>;
            },
            sorter: (a, b) => a.quota - b.quota,
        },
        {
            title: '详情',
            dataIndex: 'content',
            className: 'table-col--content',
            render: (text, record) => {
                const displayText = text && text.trim() !== '' ? text : '查看详情';

                if (!record.other || String(record.other).trim() === '') {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {displayText}
                        </Paragraph>
                    );
                }

                let otherData = null;
                try {
                    otherData = typeof record.other === 'object' ? record.other : JSON.parse(record.other);
                } catch (e) {
                    return (
                        <Tooltip content="该版本不支持显示计算详情">
                            <Paragraph
                                ellipsis={{
                                    rows: 2,
                                }}
                            >
                                {displayText}
                            </Paragraph>
                        </Tooltip>
                    );
                }

                if (!otherData) {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {displayText}
                        </Paragraph>
                    );
                }

                const isSubscriptionLog = otherData.billing_source === 'subscription'
                    || otherData.billing_mode === 'tiered_expr';

                if (
                    !isSubscriptionLog
                    && (!otherData.model_price || otherData.model_price <= 0)
                    && otherData.model_ratio === undefined
                ) {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {displayText}
                        </Paragraph>
                    );
                }

                const priceDetail = renderModelPrice(
                    Math.max(
                        0,
                        record.prompt_tokens
                        - (otherData.cache_tokens || 0)
                        - (otherData.cache_creation_tokens || 0),
                    ),
                    record.completion_tokens,
                    otherData.model_ratio,
                    otherData.model_price,
                    otherData.completion_ratio,
                    otherData.group_ratio,
                    otherData.cache_tokens || 0,
                    otherData.cache_ratio || 0,
                    otherData.cache_creation_tokens || 0,
                    otherData.cache_creation_ratio || 0,
                    { ...otherData, quota: record.quota },
                );

                return (
                    <Popover
                        content={priceDetail}
                        trigger="click"
                        position="leftTop"
                        showArrow
                    >
                        <span
                            style={{
                                color: 'var(--semi-color-primary)',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            查看详情
                        </span>
                    </Popover>
                );
            },
        }
    ];

    const exportCSV = async (e) => {
        e.stopPropagation();
        
        if (!apikey) {
            Toast.error('请先查询数据');
            return;
        }

        Toast.info('正在导出全部数据...');

        try {
            // 从后端获取全部数据（应用所有筛选条件）
            const params = new URLSearchParams({ key: apikey });
            if (dateRange && dateRange[0]) {
                params.append('startDate', Math.floor(dateRange[0].getTime() / 1000));
            }
            if (dateRange && dateRange[1]) {
                params.append('endDate', Math.floor(dateRange[1].getTime() / 1000) + 86399);
            }
            // 模型筛选
            if (modelFilter) {
                params.append('model', modelFilter);
            }
            // 流/非流筛选
            if (streamFilter !== 'all') {
                params.append('stream', streamFilter === 'stream' ? '1' : '0');
            }

            const exportRes = await API.get(`${LOG_API_BASE}/log/export?${params.toString()}`);
            const { success, data: logs } = exportRes.data;

            if (!success || !logs || logs.length === 0) {
                Toast.warning('没有可导出的数据');
                return;
            }

            const csvData = logs.map(log => {
                let cacheTokens = 0;
                let cacheCreationTokens = 0;
                try {
                    const otherData = typeof log.other === 'object' ? log.other : JSON.parse(log.other);
                    cacheTokens = otherData?.cache_tokens || 0;
                    cacheCreationTokens = otherData?.cache_creation_tokens || 0;
                } catch (e) {}
                
                return {
                    '时间': renderTimestamp(log.created_at),
                    '令牌名称': log.token_name,
                    '所属分组': log.token_group || '未设置',
                    '模型': log.model_name,
                    '用时(秒)': log.use_time,
                    '提示Tokens': log.prompt_tokens,
                    '缓存命中': cacheTokens,
                    '缓存创建': cacheCreationTokens,
                    '补全Tokens': log.completion_tokens,
                    '花费': (Number(log.quota) / 500000).toFixed(6),
                    '流式': log.is_stream ? '是' : '否',
                    '详情': log.content,
                };
            });
            const csvString = '\ufeff' + Papa.unparse(csvData);
            
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `logs_${new Date().toISOString().slice(0, 10)}.csv`;
            
            // For Safari compatibility
            if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
                link.target = '_blank';
                link.setAttribute('target', '_blank');
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
            
            Toast.success(`成功导出 ${logs.length} 条记录`);
        } catch (err) {
            Toast.error('导出失败，请稍后重试');
            console.error('Export failed:', err);
        }
    };

    const activeTabData = useMemo(() => tabData[activeTabKey] || {
        logs: [],
        balance: 0,
        usage: 0,
        accessdate: "未知",
        tokenValid: false,
        stats: null,
        tokenInfo: null,
        availableGroups: {},
    }, [tabData, activeTabKey]);

    const groupOptions = useMemo(
        () => Object.entries(activeTabData.availableGroups || {})
            .map(([value, meta]) => ({
                value,
                desc: typeof meta === 'string' ? meta : (meta?.desc || ''),
                ratio: typeof meta === 'object' ? meta?.ratio : undefined,
            }))
            .sort((a, b) => a.value.localeCompare(b.value)),
        [activeTabData.availableGroups],
    );
    const currentGroup = activeTabData.tokenInfo?.group || '';
    const selectedGroupMeta = groupOptions.find((item) => item.value === selectedGroup);
    const groupHasChanges = Boolean(selectedGroup && selectedGroup !== currentGroup);

    const statsSummary = useMemo(() => {
        // 优先使用服务器返回的完整统计数据
        const serverStats = activeTabData?.stats;
        if (serverStats) {
            const { summary, modelDistribution } = serverStats;
            const totalLogs = Number(summary?.total_logs) || 0;
            if (totalLogs === 0) return null;

            const modelCounter = {};
            const topModelEntry = modelDistribution?.[0] || { model_name: '暂无数据', count: 0 };
            modelDistribution?.forEach(m => {
                modelCounter[m.model_name] = m.count;
            });

            return {
                totalLogs,
                totalQuota: Number(summary?.total_quota) || 0,
                averageQuota: totalLogs ? (Number(summary?.total_quota) || 0) / totalLogs : 0,
                averageLatency: Number(summary?.avg_use_time) || 0,
                streamPercent: totalLogs ? ((Number(summary?.stream_count) || 0) / totalLogs) * 100 : 0,
                totalPromptTokens: Number(summary?.total_prompt_tokens) || 0,
                totalCompletionTokens: Number(summary?.total_completion_tokens) || 0,
                topModel: topModelEntry.model_name,
                topModelCount: topModelEntry.count,
                modelCounter,
            };
        }

        // 降级：基于当前页面数据计算
        const logs = activeTabData?.logs || [];
        if (!logs.length) {
            return null;
        }

        let totalQuota = 0;
        let totalPromptTokens = 0;
        let totalCompletionTokens = 0;
        let totalLatency = 0;
        let streamCount = 0;
        const modelCounter = {};

        logs.forEach((item) => {
            totalQuota += Number(item.quota) || 0;
            totalPromptTokens += Number(item.prompt_tokens) || 0;
            totalCompletionTokens += Number(item.completion_tokens) || 0;
            totalLatency += Number(item.use_time) || 0;
            streamCount += item.is_stream ? 1 : 0;
            const model = item.model_name || '未知模型';
            modelCounter[model] = (modelCounter[model] || 0) + 1;
        });

        const sortedModels = Object.entries(modelCounter).sort((a, b) => b[1] - a[1]);
        const topModelEntry = sortedModels[0] || ['暂无数据', 0];

        return {
            totalLogs: logs.length,
            totalQuota,
            averageQuota: logs.length ? totalQuota / logs.length : 0,
            averageLatency: logs.length ? totalLatency / logs.length : 0,
            streamPercent: logs.length ? (streamCount / logs.length) * 100 : 0,
            totalPromptTokens,
            totalCompletionTokens,
            topModel: topModelEntry[0],
            topModelCount: topModelEntry[1],
            modelCounter,
        };
    }, [activeTabData]);

    const modelPieOption = useMemo(() => {
        if (!statsSummary) {
            return null;
        }
        const data = Object.entries(statsSummary.modelCounter || {}).map(([model, value]) => ({ value, name: model }));
        return {
            tooltip: {
                trigger: 'item',
                borderWidth: 0,
                backgroundColor: '#111111',
                borderColor: '#262626',
                textStyle: {
                    color: '#ededed',
                    fontSize: 13,
                },
                formatter: (params) => {
                    return `${params.name}<br/>调用次数：${params.value}<br/>占比：${params.percent}%`;
                }
            },
            legend: {
                orient: 'vertical',
                left: '0',
                top: 'middle',
                textStyle: {
                    color: '#a1a1a1',
                    fontSize: 12,
                },
                icon: 'circle',
            },
            color: ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8', '#1e40af', '#075985', '#0369a1', '#0891b2'],
            series: [
                {
                    name: '模型占比',
                    type: 'pie',
                    radius: ['45%', '70%'],
                    center: ['65%', '50%'],
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: '#0a0a0a',
                        borderWidth: 2
                    },
                    label: {
                        color: '#ededed',
                        formatter: '{b}\n{c} 次',
                        fontSize: 12,
                    },
                    labelLine: {
                        length: 12,
                        length2: 8,
                        lineStyle: {
                            color: '#333333'
                        }
                    },
                    data,
                }
            ],
        };
    }, [statsSummary]);

    // 消耗趋势条形图配置
    const trendBarOption = useMemo(() => {
        const dailyStats = activeTabData?.stats?.dailyStats || [];
        if (!dailyStats.length) return null;

        // 根据精度聚合数据
        const aggregateData = (data, granularity) => {
            if (granularity === 'hour') {
                // 使用后端返回的小时统计数据
                return activeTabData?.stats?.hourlyStats || [];
            }
            
            if (granularity === 'day') return data;
            
            // 按周聚合
            const grouped = {};
            data.forEach(item => {
                const date = new Date(item.date);
                const day = date.getDay();
                const diff = date.getDate() - day + (day === 0 ? -6 : 1);
                const monday = new Date(date.setDate(diff));
                const key = monday.toISOString().split('T')[0];
                if (!grouped[key]) {
                    grouped[key] = { date: key, count: 0, quota: 0 };
                }
                grouped[key].count += Number(item.count) || 0;
                grouped[key].quota += Number(item.quota) || 0;
            });
            return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
        };

        const aggregated = aggregateData(dailyStats, trendGranularity);
        const labels = aggregated.map(d => {
            // 处理可能的 ISO 格式或 YYYY-MM-DD 格式
            const dateStr = typeof d.date === 'string' ? d.date.split('T')[0] : d.date;
            if (trendGranularity === 'hour') return d.date.slice(11, 16); // HH:00
            if (trendGranularity === 'week') return `周 ${dateStr.slice(5)}`;
            return dateStr.slice(5); // MM-DD
        });
        const quotaData = aggregated.map(d => (Number(d.quota) / 500000).toFixed(4)); // 转换为美元

        return {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#111111',
                borderColor: '#262626',
                borderWidth: 1,
                textStyle: { color: '#ededed', fontSize: 13 },
                formatter: (params) => {
                    const p = params[0];
                    return `${p.name}<br/>消耗：$${p.value}`;
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLine: { lineStyle: { color: '#333' } },
                axisLabel: { color: '#888', fontSize: 11, rotate: labels.length > 15 ? 45 : 0 },
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisLabel: { color: '#888', fontSize: 11, formatter: '${value}' },
                splitLine: { lineStyle: { color: '#222' } },
            },
            series: [{
                name: '消耗',
                type: 'bar',
                data: quotaData,
                itemStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: '#3b82f6' },
                            { offset: 1, color: '#1d4ed8' }
                        ]
                    },
                    borderRadius: [4, 4, 0, 0]
                },
                barMaxWidth: 40,
            }]
        };
    }, [activeTabData, trendGranularity]);

    // SVG 图标组件
    const icons = {
        wallet: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
        chart: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
        trending: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/><polyline points="17,6 23,6 23,12"/></svg>,
        calendar: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
        clock: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
        tag: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
        folder: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>,
        check: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22,4 12,14.01 9,11.01"/></svg>,
        zap: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2"/></svg>,
        fire: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/></svg>,
        target: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
        dollar: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
        info: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    };

    const renderContent = () => (
        <div className="query-bento">
            {/* Bento Grid 布局 */}
            <div className="query-bento-grid">
                {/* 左侧大卡片 - 搜索 */}
                <div className="query-bento-card query-bento-card--search">
                    <div className="query-bento-search">
                        <span className="query-bento-badge">TOKEN</span>
                        <h1 className="query-bento-title">令牌查询</h1>
                        <p className="query-bento-desc">输入 API Key 查询余额与调用记录</p>
                        <div className="query-bento-input-wrap">
                            <Input
                                className="query-bento-input"
                                size="large"
                                showClear
                                value={apikey}
                                onChange={(value) => setAPIKey(value)}
                                placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                prefix={<IconSearch />}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') fetchData();
                                }}
                            />
                            <Dropdown
                                trigger="click"
                                position="bottomRight"
                                className="query-history-dropdown-pop"
                                render={
                                    <div className="query-history-dropdown">
                                        {history.length === 0 ? (
                                            <div className="query-history-empty">暂无查询历史</div>
                                        ) : (
                                            <>
                                                <div className="query-history-header">
                                                    <span>最近查询 ({history.length})</span>
                                                    <Button
                                                        type="tertiary"
                                                        size="small"
                                                        theme="borderless"
                                                        onClick={clearHistory}
                                                    >
                                                        清空
                                                    </Button>
                                                </div>
                                                <div className="query-history-list">
                                                    {history.map((item) => (
                                                        <div
                                                            key={item.key}
                                                            className="query-history-item"
                                                            onClick={() => handleHistorySelect(item.key)}
                                                        >
                                                            <div className="query-history-item__main">
                                                                <span className="query-history-item__key">{maskKey(item.key)}</span>
                                                                <span className="query-history-item__balance">
                                                                    {item.balance === 100000000
                                                                        ? '∞'
                                                                        : `剩余 $${(((item.balance || 0) - (item.usage || 0)) || 0).toFixed(2)}`}
                                                                </span>
                                                            </div>
                                                            <div className="query-history-item__meta">
                                                                <span className="query-history-item__time">{formatHistoryTime(item.time)}</span>
                                                                <Button
                                                                    type="tertiary"
                                                                    theme="borderless"
                                                                    size="small"
                                                                    icon={<IconDelete />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeHistoryItem(item.key);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                }
                            >
                                <Button
                                    className="query-bento-history-btn"
                                    icon={<IconHistory />}
                                    type="tertiary"
                                    size="large"
                                    aria-label="查询历史"
                                />
                            </Dropdown>
                            <Button
                                className="query-bento-btn"
                                type='primary'
                                theme="solid"
                                onClick={fetchData}
                                loading={loading}
                                disabled={apikey === ''}
                            >
                                查询 →
                            </Button>
                        </div>
                        <p className="query-bento-tip">补全 Tokens 为 0 时自动退回余额</p>
                        {activeTabData.tokenInfo && (
                            <div className="query-key-group">
                                <div className="query-key-group__header">
                                    <div className="query-key-group__heading">
                                        <span className="query-key-group__eyebrow">KEY ROUTING</span>
                                        <div className="query-key-group__title-row">
                                            <span className="query-key-group__label">所属分组</span>
                                            <span className="query-key-group__current">
                                                <span className="query-key-group__current-dot" />
                                                {currentGroup || '未设置'}
                                            </span>
                                        </div>
                                    </div>
                                    <span
                                        className="query-key-group__name"
                                        title={activeTabData.tokenInfo.name || '未命名令牌'}
                                    >
                                        KEY · {activeTabData.tokenInfo.name || '未命名'}
                                    </span>
                                </div>

                                <div className="query-key-group__switcher">
                                    <div className="query-key-group__selection">
                                        <Select
                                            className="query-key-group__select"
                                            dropdownClassName="query-key-group__dropdown"
                                            dropdownMatchSelectWidth
                                            size="large"
                                            value={selectedGroup}
                                            onChange={setSelectedGroup}
                                            renderSelectedItem={(optionNode) => optionNode.value}
                                            disabled={groupUpdating || groupOptions.length === 0}
                                            placeholder="选择目标分组"
                                        >
                                            {groupOptions.map((option) => (
                                                <Select.Option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    <div className="query-key-group__option">
                                                        <span>{option.value}</span>
                                                        {option.desc && <small>{option.desc}</small>}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        <p className="query-key-group__desc">
                                            <span className="query-key-group__desc-dot" />
                                            {selectedGroupMeta?.desc || '请选择该 Key 可用的目标分组'}
                                        </p>
                                    </div>
                                    <Button
                                        className="query-key-group__button"
                                        type="primary"
                                        theme="solid"
                                        size="large"
                                        loading={groupUpdating}
                                        disabled={!groupHasChanges}
                                        onClick={handleGroupUpdate}
                                    >
                                        {groupHasChanges ? '确认切换' : '当前分组'}
                                    </Button>
                                </div>
                                {groupHasChanges && (
                                    <div className="query-key-group__pending">
                                        <span>{currentGroup || '未设置'}</span>
                                        <span className="query-key-group__pending-arrow">→</span>
                                        <strong>{selectedGroup}</strong>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* 右侧统计卡片 - 总额 */}
                <div className="query-bento-card query-bento-card--stat">
                    <span className="query-bento-stat-label">TOTAL</span>
                    <span className="query-bento-stat-value">
                        {activeTabData.tokenValid 
                            ? (activeTabData.balance === 100000000 ? "∞" : `$${activeTabData.balance?.toFixed(2) || '--'}`)
                            : '--'
                        }
                    </span>
                    <span className="query-bento-stat-desc">令牌总额</span>
                </div>

                {/* 剩余额度 */}
                <div className="query-bento-card query-bento-card--stat query-bento-card--highlight">
                    <span className="query-bento-stat-label">REMAINING</span>
                    <span className="query-bento-stat-value">
                        {activeTabData.tokenValid 
                            ? (activeTabData.balance === 100000000 ? "∞" : `$${(activeTabData.balance - activeTabData.usage)?.toFixed(2) || '--'}`)
                            : '--'
                        }
                    </span>
                    <span className="query-bento-stat-desc">剩余额度</span>
                </div>

                {/* 已用额度 */}
                <div className="query-bento-card query-bento-card--stat">
                    <span className="query-bento-stat-label">USED</span>
                    <span className="query-bento-stat-value">
                        {activeTabData.tokenValid 
                            ? `$${activeTabData.usage?.toFixed(2) || '--'}`
                            : '--'
                        }
                    </span>
                    <span className="query-bento-stat-desc">已用额度</span>
                </div>

                {/* 有效期 */}
                <div className="query-bento-card query-bento-card--stat">
                    <span className="query-bento-stat-label">EXPIRES</span>
                    <span className="query-bento-stat-value query-bento-stat-value--sm">
                        {activeTabData.tokenValid 
                            ? (activeTabData.accessdate === 0 ? '永久' : '有限期')
                            : '--'
                        }
                    </span>
                    <span className="query-bento-stat-desc">有效期</span>
                </div>
            </div>

            {/* 数据可视化面板 */}
            {statsSummary ? (
                <section className="query-viz-section">
                    <div className="query-viz-card">
                        <div className="query-viz-header">
                            <div className="query-viz-header__text">
                                <span className="query-viz-label">ANALYTICS</span>
                                <h3 className="query-viz-title">数据分析</h3>
                            </div>
                            <div className="query-viz-header__controls">
                                {chartType === 'bar' && (
                                    <div className="query-viz-granularity">
                                        <Button 
                                            size="small"
                                            type={trendGranularity === 'hour' ? 'secondary' : 'tertiary'}
                                            onClick={() => setTrendGranularity('hour')}
                                        >
                                            时
                                        </Button>
                                        <Button 
                                            size="small"
                                            type={trendGranularity === 'day' ? 'secondary' : 'tertiary'}
                                            onClick={() => setTrendGranularity('day')}
                                        >
                                            日
                                        </Button>
                                        <Button 
                                            size="small"
                                            type={trendGranularity === 'week' ? 'secondary' : 'tertiary'}
                                            onClick={() => setTrendGranularity('week')}
                                        >
                                            周
                                        </Button>
                                    </div>
                                )}
                                <div className="query-viz-chart-toggle">
                                    <Button 
                                        size="small"
                                        type={chartType === 'pie' ? 'primary' : 'tertiary'}
                                        onClick={() => setChartType('pie')}
                                    >
                                        模型分布
                                    </Button>
                                    <Button 
                                        size="small"
                                        type={chartType === 'bar' ? 'primary' : 'tertiary'}
                                        onClick={() => setChartType('bar')}
                                    >
                                        消耗趋势
                                    </Button>
                                </div>
                                <span className="query-viz-badge">{statsSummary.totalLogs} 条记录</span>
                            </div>
                        </div>
                        <div className="query-viz-body">
                            <div className="query-viz-chart">
                                <Suspense fallback={<div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>}>
                                    {chartType === 'pie' ? (
                                        <ReactECharts style={{ height: 320, background: 'transparent' }} option={{...modelPieOption, backgroundColor: 'transparent'}} notMerge lazyUpdate />
                                    ) : (
                                        trendBarOption ? (
                                            <ReactECharts style={{ height: 320, background: 'transparent' }} option={{...trendBarOption, backgroundColor: 'transparent'}} notMerge lazyUpdate />
                                        ) : (
                                            <div className="query-viz-empty" style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <p style={{ color: 'var(--text-tertiary)' }}>暂无趋势数据</p>
                                            </div>
                                        )
                                    )}
                                </Suspense>
                            </div>
                            <div className="query-viz-metrics">
                                <div className="query-viz-metric">
                                    <div className="query-viz-metric__icon">{icons.dollar}</div>
                                    <div className="query-viz-metric__body">
                                        <span className="query-viz-metric__label">COST</span>
                                        <span className="query-viz-metric__value">{renderQuota(statsSummary.totalQuota, 3)}</span>
                                        <span className="query-viz-metric__note">平均 {renderQuota(statsSummary.averageQuota, 5)}/次</span>
                                    </div>
                                </div>
                                <div className="query-viz-metric">
                                    <div className="query-viz-metric__icon">{icons.zap}</div>
                                    <div className="query-viz-metric__body">
                                        <span className="query-viz-metric__label">STREAM</span>
                                        <span className="query-viz-metric__value">{statsSummary.streamPercent.toFixed(1)}%</span>
                                        <span className="query-viz-metric__note">平均 {statsSummary.averageLatency.toFixed(1)}s</span>
                                    </div>
                                </div>
                                <div className="query-viz-metric">
                                    <div className="query-viz-metric__icon">{icons.fire}</div>
                                    <div className="query-viz-metric__body">
                                        <span className="query-viz-metric__label">TOP MODEL</span>
                                        <span className="query-viz-metric__value query-viz-metric__value--sm">{statsSummary.topModel}</span>
                                        <span className="query-viz-metric__note">{statsSummary.topModelCount} 次调用</span>
                                    </div>
                                </div>
                                <div className="query-viz-metric">
                                    <div className="query-viz-metric__icon">{icons.target}</div>
                                    <div className="query-viz-metric__body">
                                        <span className="query-viz-metric__label">TOKENS</span>
                                        <span className="query-viz-metric__value query-viz-metric__value--sm">
                                            {(statsSummary.totalPromptTokens + statsSummary.totalCompletionTokens).toLocaleString()}
                                        </span>
                                        <span className="query-viz-metric__note">
                                            提示 {statsSummary.totalPromptTokens.toLocaleString()} · 补全 {statsSummary.totalCompletionTokens.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            ) : (
                activeTabData.tokenValid && (
                    <section className="query-viz-section">
                        <div className="query-viz-card query-viz-card--empty">
                            <div className="query-viz-empty">
                                <div className="query-viz-empty__icon">{icons.chart}</div>
                                <h3 className="query-viz-empty__title">暂无数据</h3>
                                <p className="query-viz-empty__text">查询完成后将生成数据分析</p>
                            </div>
                        </div>
                    </section>
                )
            )}

            {/* 调用明细表格 - Bento 风格 */}
            {activeTabData.tokenValid && (
                <section className="query-logs-section">
                    <div className="query-logs-card">
                        <div className="query-logs-header">
                            <div className="query-logs-header__left">
                                <span className="query-logs-badge">{showTransferHistory ? 'TRANSFER' : 'LOGS'}</span>
                                <h3 className="query-logs-title">{showTransferHistory ? '转账记录' : '调用明细'}</h3>
                                <span className="query-logs-count">
                                    {showTransferHistory ? transferPagination.total : totalLogs} 条
                                </span>
                            </div>
                            <div className="query-logs-header__right">
                                <Button 
                                    type={showTransferHistory ? 'primary' : 'tertiary'}
                                    icon={<IconHistory />}
                                    onClick={() => {
                                        const newValue = !showTransferHistory;
                                        setShowTransferHistory(newValue);
                                        if (newValue && transferRecords.length === 0) {
                                            fetchTransferHistory(1);
                                        }
                                    }}
                                >
                                    转账记录
                                </Button>
                                {!showTransferHistory && (
                                    <Button 
                                        className="query-logs-export" 
                                        icon={<IconDownload />} 
                                        onClick={(e) => exportCSV(e)} 
                                        disabled={activeTabData.logs.length === 0}
                                    >
                                        导出
                                    </Button>
                                )}
                            </div>
                        </div>
                        
                        {/* 筛选工具栏 - 仅在调用明细时显示 */}
                        {!showTransferHistory && (
                            <FilterToolbar
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                modelFilter={modelFilter}
                                setModelFilter={setModelFilter}
                                streamFilter={streamFilter}
                                setStreamFilter={setStreamFilter}
                                availableModels={availableModels}
                                onFilterChange={() => {
                                    setCurrentPage(1);
                                    setShouldAutoFetch(true);
                                }}
                            />
                        )}
                        
                        <div className="query-logs-body">
                            {showTransferHistory ? (
                                <Spin spinning={transferLoading}>
                                    {transferRecords.length > 0 ? (
                                        <Table
                                            className="query-logs-table"
                                            columns={transferColumns}
                                            dataSource={transferRecords}
                                            rowKey="id"
                                            pagination={{
                                                currentPage: transferPagination.page,
                                                pageSize: 20,
                                                total: transferPagination.total,
                                                onPageChange: (page) => fetchTransferHistory(page),
                                                showTotal: (total) => `共 ${total} 条`,
                                            }}
                                        />
                                    ) : (
                                        <Empty title="暂无转账记录" description="该令牌没有转入或转出记录" />
                                    )}
                                </Spin>
                            ) : (
                                <Spin spinning={loading}>
                                    <Table
                                        className="query-logs-table"
                                        columns={columns}
                                        dataSource={activeTabData.logs}
                                        pagination={{
                                            currentPage: currentPage,
                                            pageSize: pageSize,
                                            total: totalLogs,
                                            showSizeChanger: true,
                                            pageSizeOpts: [10, 20, 50, 100],
                                            onPageChange: (page) => {
                                                setCurrentPage(page);
                                                setTimeout(() => fetchData(), 0);
                                            },
                                            onPageSizeChange: (size) => {
                                                setPageSize(size);
                                                setCurrentPage(1);
                                                setTimeout(() => fetchData(), 0);
                                            },
                                            showTotal: (total) => `共 ${total} 条`,
                                            showQuickJumper: true,
                                        }}
                                    />
                                </Spin>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );

    return (
        <div className="log-page">
            {Object.keys(baseUrls).length > 1 ? (
                <Tabs className="wyz-tabs" type="line" onChange={handleTabChange}>
                    {Object.entries(baseUrls).map(([key, url]) => (
                        <TabPane tab={key} itemKey={key} key={key}>
                            {renderContent()}
                        </TabPane>
                    ))}
                </Tabs>
            ) : (
                renderContent()
            )}
        </div>
    );
};

export default LogsTable;
